import { Box, Flex, Image, useToast, Center, Button, Text } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import Layout from "../../components/app/Layout";
import BaseContainer from "../../components/form/BaseContainer";
import { DropdownInput } from "../../components/form/controls/DropdownInput";
import { FormInput } from "../../components/form/controls/FormInput";
import { TextAreaInput } from "../../components/form/controls/TextAreaInput";
import { Loading } from "../../components/shared/Loading";
import { Separator } from "../../components/shared/Separator";
import { SupportService } from "../../service/support.service";
import { store } from "../../store";
import { colors } from "../../constants/design.constants";

const baseContainerStyles = {
  flexGrow: "1",
  borderRadius: "2px",
  minW: "95%",
  paddingBottom: "50px",
};

const supportService = new SupportService();

export const Support: React.FC<any> = () => {
  const toast = useToast();

  const userEmail = store.getState().userProfile.email;

  const [loading, setLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState(false);
  const [sending, setSending] = useState(false);
  const [formState, setFormState] = useState({});

  const onInputChange = (event: any) => {
    let { name, value } = event.target;

    setFormState({
      ...formState,
      [name]: value,
    });
  };

  const init = async () => {};

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = { ...formState, email: userEmail };
    setSending(true);
    await supportService
      .send(payload)
      .then(() => {
        setSendSuccess(true);
        setFormState({});
      })
      .catch(() => alert("error"))
      .finally(() => setSending(false));
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Layout>
      <Loading loading={loading} />

      <Box
        bg={colors.elevated}
        m="2.5%"
        mb="0"
        borderRadius="2px"
        border={`1px solid ${colors.border}`}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          inset={0}
          opacity={0.5}
          backgroundImage={`
            radial-gradient(ellipse 50% 80% at 90% 20%, rgba(94, 184, 212, 0.08), transparent),
            linear-gradient(${colors.borderSubtle} 1px, transparent 1px),
            linear-gradient(90deg, ${colors.borderSubtle} 1px, transparent 1px)
          `}
          backgroundSize="100% 100%, 40px 40px, 40px 40px"
          pointerEvents="none"
        />
        <Flex
          color="white"
          my="28px"
          flexDir="column"
          gap="8px"
          position="relative"
          zIndex={1}
        >
          <Center>
            <Flex flexDir="column" alignItems="center" gap="12px">
              <Text
                fontSize="10px"
                fontWeight="500"
                letterSpacing="0.18em"
                textTransform="uppercase"
                color={colors.signal}
              >
                Sumlookup
              </Text>
              <Flex flexDir="row" gap="16px" alignItems="center">
                <Image
                  src="/assets/company-logo/sumlookup.png"
                  alt="Sumlookup Branding"
                  width="200px"
                  height="auto"
                  filter="brightness(1.1)"
                />
                <Box
                  fontSize="28px"
                  fontWeight="500"
                  letterSpacing="0.12em"
                  color={colors.textPrimary}
                >
                  SUPPORT
                </Box>
              </Flex>
            </Flex>
          </Center>
          <Center>
            <Box
              mt="12px"
              textAlign="center"
              w="60%"
              fontSize="14px"
              fontWeight="400"
              color={colors.textSecondary}
              lineHeight="1.6"
            >
              We are committed to providing an exceptional platform experience
              for all our clients. Should you encounter any concerns, have
              valuable suggestions, or seek clarification, our team will be
              happy to assist you.
              <br />
              <br />
              <Box as="span" fontWeight="500" color={colors.signal}>
                — SUMLOOKUP Team
              </Box>
            </Box>
          </Center>
        </Flex>
      </Box>
      <BaseContainer styles={baseContainerStyles}>
        <Flex flexDir="row" gap="24px">
          <Box
            p="12px"
            w="60%"
            color={colors.textPrimary}
            borderRight={`1px solid ${colors.border}`}
          >
            <Flex flexDir="column" gap="6px">
              <Box fontSize="18px" fontWeight="500" letterSpacing="-0.01em">
                Frequently asked questions
                <Separator />
              </Box>
              <QandA
                question="What is the Lighthouse used for?"
                answer="The Lighthouse aims to provide our clients with as much visibility as possible when it comes to their integrations."
              />
              <QandA
                question="How do I trigger an integration?"
                answer="On the sidebar, you will see a section 'Manual Trigger'. On that page, you will see a list of all your active integrations and their last run status. Click 'Run' to trigger an integration. Please ensure you are aware of the integration and how it works before triggering it."
              />
              <QandA
                question="What are 'Archives & Logs'?"
                answer="In order to provide as much visibility as possible, we have made the most recently uploaded (to Anaplan) file, and any error logs available for download. The user can directly see how the file was transformed and loaded, as well as debug any error logs."
              />
              <QandA
                question="What information is available in the 'Dashboard'?"
                answer="'Dashboard' gives the handy analytics of all your integrations in one place. The stats are dated to the first of the current month to now. Please keep in mind we are actively working on adding more analytics and features."
              />
            </Flex>
          </Box>
          <Box w="40%" pr="20px">
            <Box p="12px" fontSize="18px" fontWeight="500" letterSpacing="-0.01em">
              Get in touch
              <Separator />
            </Box>

            <form onSubmit={handleSubmit}>
              <Flex p="12px" flexDirection="column" gap="12px">
                <FormInput
                  name="name"
                  title="Name"
                  placeholder="Your name"
                  required
                  onChange={onInputChange}
                />
                <DropdownInput
                  name="type"
                  title="Type"
                  required
                  options={{
                    feature: "Feature Request",
                    bug: "Bug Report",
                    feedback: "Feedback",
                  }}
                  defaultValue="feature"
                />
                <FormInput
                  name="subject"
                  title="Subject"
                  placeholder="What is this about?"
                  required
                  onChange={onInputChange}
                />
                <TextAreaInput
                  name="message"
                  title="Message"
                  placeholder="Your message"
                  required
                  onChange={onInputChange}
                />
              </Flex>
              <Button
                hidden={sendSuccess}
                isDisabled={sending}
                type="submit"
                ml="12px"
                mt="12px"
                h="40px"
                px="24px"
                color={colors.void}
                bg={colors.signal}
                borderRadius="2px"
                fontSize="12px"
                fontWeight="500"
                letterSpacing="0.08em"
                textTransform="uppercase"
                _hover={{ bg: "#7AC8DE" }}
              >
                Send
              </Button>
              <Box
                hidden={!sendSuccess}
                p="12px"
                fontSize="15px"
                fontWeight="500"
                color={colors.signal}
              >
                Thanks — we'll be in touch soon.
              </Box>
            </form>
          </Box>
        </Flex>
      </BaseContainer>
    </Layout>
  );
};

const QandA = ({ question, answer }) => {
  return (
    <Flex flexDir="column" gap="6px" my="14px">
      <Box fontSize="14px" fontWeight="500" color={colors.textPrimary}>
        {question}
      </Box>
      <Box fontSize="13px" fontWeight="400" color={colors.textSecondary} lineHeight="1.55">
        {answer}
      </Box>
    </Flex>
  );
};
